/* The lab screens (docs/plans/phase1-lab.md §8). Tokens only; danger for critical only. */

.page {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
}

.section {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.sectionHeading {
  font: var(--t-heading);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: var(--space-3);
}

.toolbar > :first-child {
  flex: 0 1 22rem;
}

/* Lab sections as a segmented control (ADR 0019). */
.tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  width: fit-content;
  max-width: 100%;
  padding: 3px;
  border-radius: var(--radius-control);
  background: var(--wash);
}

.tab {
  display: inline-flex;
  align-items: center;
  min-height: calc(var(--control-height) - 8px);
  padding: 0 var(--space-3);
  border-radius: calc(var(--radius-control) - 2px);
  color: var(--pencil);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
  font: var(--t-body);
}

.tab:hover {
  color: var(--ink);
}

.tab[aria-current='page'] {
  background: var(--paper);
  box-shadow: var(--shadow-pill);
  color: var(--brand);
  font-weight: 500;
}

.tab:focus-visible {
  outline: var(--focus-ring);
  outline-offset: 2px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.meta {
  color: var(--graphite);
  font: var(--t-small);
}

.stack {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.row {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.row > * {
  flex: 1 1 10rem;
  min-width: 0;
}

.strong {
  font-weight: 600;
}

.critical {
  color: var(--danger);
  font-weight: 600;
}

.numbers {
  font-variant-numeric: tabular-nums;
}

/* Result entry: one line per analyte, the range and previous result beside the field. */
.entryHeading {
  margin-top: var(--space-3);
  color: var(--graphite);
  font: var(--t-body-strong);
}

.entry {
  display: grid;
  grid-template-columns: minmax(12rem, 22rem) minmax(8rem, 1fr) 8rem;
  align-items: start;
  gap: var(--space-2) var(--space-4);
  padding: var(--space-3) 0;
  border-bottom: 1px solid var(--rule);
}

/* On a phone the range and flag go under the field instead of beside it. */
@media (width < 768px) {
  .entry {
    grid-template-columns: minmax(0, 1fr);
  }
}

.entryAside {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  padding-top: var(--space-5);
  color: var(--graphite);
  font: var(--t-small);
}

.flag {
  padding-top: var(--space-5);
  font: var(--t-body-strong);
}

.flagWord {
  font: var(--t-small);
}

.flag[data-critical] {
  color: var(--danger);
}

.list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
}

.listRow {
  display: grid;
  grid-template-columns: minmax(10rem, 16rem) 1fr auto;
  align-items: center;
  gap: var(--space-4);
  min-height: 44px;
  padding: var(--space-2) 0;
  border-bottom: 1px solid var(--rule);
}

/* Every section's table has the same columns at the same widths, so the eye runs straight
 * down the page from one section to the next (DESIGN.md §8, Lab worklist). */
.worklistTable table[role='grid'] {
  min-width: 44rem;
  table-layout: fixed;
}

.worklistTable th:nth-child(1) {
  width: 8.5rem;
}

.worklistTable th:nth-child(2) {
  width: 20%;
}

.worklistTable th:nth-child(4) {
  width: 6rem;
}

.worklistTable th:nth-child(5) {
  width: 6rem;
}

.worklistTable th:nth-child(6) {
  width: 6.5rem;
}

.worklistTable th:nth-child(7) {
  width: 13rem;
}

.overTarget {
  display: block;
}

.worklistTable td {
  overflow-wrap: anywhere;
}

/* The sample's facts: label above value, as many to a row as fit, one rule under the lot. */
.factGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(15rem, 1fr));
  gap: var(--space-4) var(--space-5);
  margin: 0;
  padding: var(--space-5);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  background: var(--paper);
  box-shadow: var(--shadow-card);
}

@media (width < 640px) {
  .factGrid {
    padding: var(--space-4);
  }
}

.factGrid dd {
  margin: 0;
}

/* Views of one area: a segmented control made of links (ADR 0019). */
.subnav {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  width: fit-content;
  max-width: 100%;
  padding: 3px;
  border-radius: var(--radius-control);
  background: var(--wash);
}

.subnav a {
  display: inline-flex;
  align-items: center;
  min-height: calc(var(--control-height) - 8px);
  padding: 0 var(--space-3);
  border-radius: calc(var(--radius-control) - 2px);
  color: var(--pencil);
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color var(--dur) var(--ease),
    color var(--dur) var(--ease);
}

.subnav a:hover {
  color: var(--ink);
}

.subnav a[aria-current='page'] {
  background: var(--paper);
  box-shadow: var(--shadow-pill);
  color: var(--brand);
  font-weight: 500;
}

/* Labels: each 50 × 25 mm label is a page of its own. */
.labelRun {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
}

.labelRun > * {
  break-after: page;
}

.labelName {
  overflow: hidden;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.labelLine {
  display: flex;
  justify-content: space-between;
  gap: 2mm;
  overflow: hidden;
  white-space: nowrap;
}

.printNote {
  margin: 0 0 3mm;
}

.preview {
  max-height: 60vh;
  overflow: auto;
}

/* Printed on demand but not shown: off screen on screens, in place on paper. */
@media screen {
  .offscreen {
    position: absolute;
    left: -10000px;
  }
}
